import { Control, useController, useFieldArray } from 'react-hook-form';

import { ComplianceFormValues } from './types';
import {
  createMaxLengthValidator,
  createMinLengthValidator,
  createRequiredValidator,
  isOptionRequired,
  isValidInput,
} from '../../validators';

export const useControllers = (
  control: Control<ComplianceFormValues, any, ComplianceFormValues>
) => {
  const {
    fields: samples,
    append: addSample,
    remove: removeSample,
  } = useFieldArray({
    control,
    name: 'samples',
  });
  const {
    field: { value: subscriberOptin, onChange: setSubscriberOptin },
    fieldState: { error: subscriberOptinError },
  } = useController({
    control,
    name: 'subscriberOptin',
    rules: {
      validate: {
        isOptionRequired,
      },
    },
  });
  const {
    field: { value: optinKeywords, onChange: setOptinKeywords },
  } = useController({
    control,
    name: 'optinKeywords',
  });
  const {
    field: {
      value: optinMessage,
      onChange: setOptinMessage,
      onBlur: validateOptinMessage,
    },
    fieldState: { error: optinMessageError },
  } = useController({
    control,
    name: 'optinMessage',
    disabled: !subscriberOptin,
    rules: {
      validate: {
        isValidInput,
        withValidMinLength: createMinLengthValidator(20, true),
        withValidMaxLength: createMaxLengthValidator(1024, true),
      },
    },
  });
  const {
    field: { value: subscriberOptout, onChange: setSubscriberOptout },
    fieldState: { error: subscriberOptoutError },
  } = useController({
    control,
    name: 'subscriberOptout',
    rules: {
      validate: {
        isOptionRequired,
      },
    },
  });
  const {
    field: { value: optoutKeywords, onChange: setOptoutKeywords },
  } = useController({
    control,
    name: 'optoutKeywords',
  });
  const {
    field: {
      value: optoutMessage,
      onChange: setOptoutMessage,
      onBlur: validateOptoutMessage,
    },
    fieldState: { error: optoutMessageError },
  } = useController({
    control,
    name: 'optoutMessage',
    disabled: !subscriberOptout,
    rules: {
      validate: {
        isValidInput,
        isRequired: createRequiredValidator('Please enter message'),
        withValidMinLength: createMinLengthValidator(20),
        withValidMaxLength: createMaxLengthValidator(1024),
      },
    },
  });
  const {
    field: { value: subscriberHelp, onChange: setSubscriberHelp },
    fieldState: { error: subscriberHelpError },
  } = useController({
    control,
    name: 'subscriberHelp',
    rules: {
      validate: {
        isOptionRequired,
      },
    },
  });
  const {
    field: { value: helpKeywords, onChange: setHelpKeywords },
  } = useController({
    control,
    name: 'helpKeywords',
  });
  const {
    field: {
      value: helpMessage,
      onChange: setHelpMessage,
      onBlur: validateHelpMessage,
    },
    fieldState: { error: helpMessageError },
  } = useController({
    control,
    name: 'helpMessage',
    disabled: !subscriberHelp,
    rules: {
      validate: {
        isValidInput,
        isRequired: createRequiredValidator('Please enter message'),
        withValidMinLength: createMinLengthValidator(20),
        withValidMaxLength: createMaxLengthValidator(1024),
      },
    },
  });
  const {
    field: { value: directLending, onChange: setDirectLending },
    fieldState: { error: directLendingError },
  } = useController({
    control,
    name: 'directLending',
    rules: {
      validate: {
        isOptionRequired,
      },
    },
  });
  const {
    field: { value: embeddedLink, onChange: setEmbeddedLink },
    fieldState: { error: embeddedLinkError },
  } = useController({
    control,
    name: 'embeddedLink',
    rules: {
      validate: {
        isOptionRequired,
      },
    },
  });
  const {
    field: { value: embeddedPhone, onChange: setEmbeddedPhone },
    fieldState: { error: embeddedPhoneError },
  } = useController({
    control,
    name: 'embeddedPhone',
    rules: {
      validate: {
        isOptionRequired,
      },
    },
  });
  const {
    field: { value: ageGated, onChange: setAgeGated },
    fieldState: { error: ageGatedError },
  } = useController({
    control,
    name: 'ageGated',
    rules: {
      validate: {
        isOptionRequired,
      },
    },
  });
  const {
    field: { value: cnpUid, onChange: setCnpUid },
    fieldState: { error: cnpUidError },
  } = useController({
    control,
    name: 'cnpUid',
    rules: {
      validate: {
        isRequired: createRequiredValidator(
          'Please select your Connectivity Partner'
        ),
      },
    },
  });
  const {
    field: { value: resellerUid, onChange: setResellerUid },
    fieldState: { error: resellerUidError },
  } = useController({
    control,
    name: 'resellerUid',
    rules: {
      validate: {
        isRequired: createRequiredValidator('Please select a Reseller'),
      },
    },
  });

  const {
    field: {
      value: embeddedLinkSample,
      onChange: setEmbeddedLinkSample,
      onBlur: validateEmbeddedLinkSample,
    },
    fieldState: { error: embeddedLinkSampleError },
  } = useController({
    control,
    name: 'embeddedLinkSample',
    disabled: !embeddedLink,
    rules: {
      validate: {
        isValidInput,
        withValidMaxLength: createMaxLengthValidator(255),
      },
    },
  });

  const {
    field: {
      value: fallbackCampaignUid,
      onChange: setFallbackCampaignUid,
      onBlur: validateFallbackCampaignUid,
    },
    fieldState: { error: fallbackCampaignUidError },
  } = useController({
    control,
    name: 'fallbackCampaignUid',
  });

  return {
    samples,
    addSample,
    removeSample,
    subscriberOptin,
    setSubscriberOptin,
    subscriberOptinError,
    optinKeywords,
    setOptinKeywords,
    optinMessage,
    setOptinMessage,
    validateOptinMessage,
    optinMessageError,
    subscriberOptout,
    setSubscriberOptout,
    subscriberOptoutError,
    optoutKeywords,
    setOptoutKeywords,
    optoutMessage,
    setOptoutMessage,
    validateOptoutMessage,
    optoutMessageError,
    subscriberHelp,
    setSubscriberHelp,
    subscriberHelpError,
    helpKeywords,
    setHelpKeywords,
    helpMessage,
    setHelpMessage,
    validateHelpMessage,
    helpMessageError,
    directLending,
    setDirectLending,
    directLendingError,
    embeddedLink,
    setEmbeddedLink,
    embeddedLinkError,
    embeddedLinkSample,
    embeddedLinkSampleError,
    setEmbeddedLinkSample,
    validateEmbeddedLinkSample,
    embeddedPhone,
    setEmbeddedPhone,
    embeddedPhoneError,
    ageGated,
    setAgeGated,
    ageGatedError,
    cnpUid,
    setCnpUid,
    cnpUidError,
    resellerUid,
    setResellerUid,
    resellerUidError,
    fallbackCampaignUid,
    setFallbackCampaignUid,
    fallbackCampaignUidError,
    validateFallbackCampaignUid,
  };
};
